// ui-kit: hand-built
import confetti from 'canvas-confetti';
import {
    useEffect,
    useRef,
    type ComponentPropsWithRef,
    type CSSProperties,
    type ReactNode,
    type Ref,
} from 'react';
import {
    RiAlertFill,
    RiCheckboxCircleFill,
    RiCloseFill,
    RiForbid2Fill,
    RiInformationFill,
    RiStarSmileFill,
} from '@remixicon/react';

import { classNames } from '../../internal/classNames';

import Avatar from '../Avatar/Avatar';
import IconAction from '../IconAction/IconAction';
import type { IconSource } from '../Icon/Icon';
import MoreOptions, {
    type MoreOptionsItemProps,
    type MoreOptionsProps,
} from '../MoreOptions/MoreOptions';

export const ALERT_VARIANTS = ['info', 'warning', 'error', 'success', 'celebration'] as const;
export type AlertVariant = (typeof ALERT_VARIANTS)[number];

export type AlertProps = Omit<ComponentPropsWithRef<'div'>, 'children'> & {
    variant?: AlertVariant;
    onClose?: () => void;
    /** Primary text action (Button). Shown inline on wide layout; full-width when compact. */
    action?: ReactNode;
    /** Overflow menu slot (typically `<MoreOptions>` or `<Alert.MoreOptions>`). */
    moreOptions?: ReactNode;
    icon?: IconSource;
    /** Celebration only: fire the confetti animation. Default `true`. */
    confetti?: boolean;
    children?: ReactNode;
    ref?: Ref<HTMLDivElement>;
    'data-test-component'?: string;
};

const VARIANT_ICONS: Record<AlertVariant, IconSource> = {
    info: RiInformationFill,
    warning: RiAlertFill,
    error: RiForbid2Fill,
    success: RiCheckboxCircleFill,
    celebration: RiStarSmileFill,
};

const ALERT_ICON_STYLE = {
    '--avatar-background': 'var(--raised-color)',
    '--avatar-icon-color': 'var(--icon-core-primary)',
} as CSSProperties;

const CELEBRATION_COLORS = [
    '#4040E0',
    '#002080',
    '#0080E0',
    '#2040C0',
    '#80A0E0',
    '#C0C0E0',
    '#4060E0',
];

function CelebrationConfetti() {
    const canvasRef = useRef<HTMLCanvasElement>(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        // jsdom and other non-browser canvases have no 2d context.
        if (!canvas?.getContext?.('2d')) {
            return;
        }

        const fire = confetti.create(canvas, { resize: true, useWorker: false });

        const shapes: confetti.Shape[] = ['square', 'circle'];
        try {
            shapes.push(
                confetti.shapeFromPath({
                    path: 'M1,0 Q8,9 1,18 Q-6,27 1,36 L3,36 Q10,27 3,18 Q-4,9 3,0 Z',
                }),
            );
        } catch {
            // Path shapes need Path2D (unavailable in some environments).
        }

        const BURST_BASE = {
            gravity: 0.3,
            scalar: 0.5,
            ticks: 220,
            origin: { x: 0.5, y: 1.3 },
            colors: CELEBRATION_COLORS,
            shapes,
            flat: false,
        };

        let delayId: ReturnType<typeof setTimeout> | undefined;

        function burst() {
            fire({
                ...BURST_BASE,
                particleCount: 45,
                angle: 80,
                spread: 40,
                startVelocity: 10,
                drift: 0.5,
                origin: { x: 0.0, y: 1.3 },
            });
            delayId = setTimeout(() => {
                fire({
                    ...BURST_BASE,
                    particleCount: 35,
                    angle: 100,
                    spread: 35,
                    startVelocity: 12,
                    drift: 0.5,
                    origin: { x: 0.15, y: 1.3 },
                });
            }, 100);
        }

        burst();
        const id = setInterval(burst, 7000);
        return () => {
            clearInterval(id);
            clearTimeout(delayId);
            fire.reset();
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            className='ui-alert__confetti'
            data-test-name='confetti'
        />
    );
}

function Alert({
    variant = 'info',
    children,
    className = '',
    action,
    moreOptions,
    onClose,
    icon,
    confetti = true,
    ref,
    'data-test-component': testComponent = 'ui-kit/Alert',
    ...rest
}: AlertProps) {
    return (
        <div
            {...rest}
            ref={ref}
            role='status'
            className={classNames({
                'ui-alert': true,
                [`ui-alert--${variant}`]: true,
                [className]: Boolean(className),
            })}
            data-test-component={testComponent}>
            {variant === 'celebration' && confetti ? <CelebrationConfetti /> : null}
            <div className='ui-alert__main'>
                <div className='ui-alert__content'>
                    <Avatar
                        size='medium'
                        icon={icon ?? VARIANT_ICONS[variant]}
                        className='ui-alert__icon'
                        style={ALERT_ICON_STYLE}
                        data-test-name='icon'
                    />
                    <div
                        className='ui-alert__text'
                        data-test-name='text'>
                        {children}
                    </div>
                </div>
                {action ? (
                    <div
                        className='ui-alert__actions'
                        data-test-name='actions'>
                        {action}
                    </div>
                ) : null}
                {moreOptions ? (
                    <div
                        className='ui-alert__options'
                        data-test-name='more-options'>
                        {moreOptions}
                    </div>
                ) : null}
                {onClose ? (
                    <IconAction
                        icon={RiCloseFill}
                        title='Close'
                        aria-label='Close'
                        onClick={onClose}
                        data-test-name='close'
                    />
                ) : null}
            </div>
            {action ? (
                <div
                    className='ui-alert__mobile-actions'
                    data-test-name='mobile-actions'>
                    {action}
                </div>
            ) : null}
        </div>
    );
}

Alert.displayName = 'Alert';

Alert.MoreOptions = function AlertMoreOptions({
    title = 'More actions',
    options,
    ...rest
}: Omit<MoreOptionsProps, 'children'> & {
    options?: MoreOptionsItemProps[];
}) {
    return (
        <MoreOptions
            title={title}
            {...rest}>
            {options?.map((option, index) => (
                <MoreOptions.Item
                    key={typeof option.label === 'string' ? option.label : index}
                    {...option}
                />
            ))}
        </MoreOptions>
    );
};

export function Celebration(props: Omit<AlertProps, 'variant'>) {
    return (
        <Alert
            {...props}
            variant='celebration'
        />
    );
}

export default Alert;
